const blogPosts = [
  {
    slug: "conversations-are-data",
    title: "Conversations Are Data. Start Treating Them That Way.",
    date: "March 19, 2026",
    readTime: "5 min read",
    persona: "VC / M&A / Dealmakers",
    keywords: [
      "conversation intelligence",
      "AI meeting intelligence",
      "meeting intelligence for investors",
      "second brain on every call",
      "high-stakes meeting AI",
    ],
    excerpt:
      "Conversation Intelligence is a new category. It lives where revenue is on the line, due diligence runs across sessions, and a missed signal costs you millions. Here's what it does — and where it's going.",
    sections: [
      // ── 1. OPENING — Grand category intro ──────────────────────────────
      {
        type: "quote",
        content:
          "Every significant outcome traces back to a conversation. Not a spreadsheet. Not a model. A conversation.",
      },
      {
        type: "body",
        content:
          "For decades, professionals have invested in better data, better models, faster analytics. And they should. But there's a category of intelligence that has never been captured, never been systematised, and never been treated as the asset it is.",
      },
      {
        type: "body",
        content:
          "The conversation. The call. The room where the real work happens.",
      },
      {
        type: "body",
        content:
          "Conversation Intelligence is the category being built to change that. It's not a feature. It's not a productivity tool. It's the missing layer between what was said and what gets decided — and it's emerging now because the technology has finally caught up to the problem.",
      },

      // ── 2. WHERE IT SITS ───────────────────────────────────────────────
      {
        type: "heading",
        content: "Where It Sits",
      },
      {
        type: "body",
        content:
          "This category doesn't live in operational meetings or team stand-ups. It lives in the conversations where missing a signal costs you millions.",
      },
      {
        type: "bullets",
        items: [
          "VC / PE due diligence — you're running five calls with the same founding team over six weeks. Context accumulates. Answers drift. Without cross-session intelligence, you're making a million-dollar decision on fragmented notes.",
          "Enterprise sales — revenue is on the line. Objections are telegraphed early, often subtly. A missed signal in call two becomes a lost deal in call seven.",
          "M&A negotiations — counterparties shift positions. Concession windows open and close inside a single conversation. The person who's tracking it wins.",
          "Key account reviews — a client drops a competitor's name almost off-hand. You don't catch it. Three months later, you've lost the account.",
        ],
      },
      {
        type: "body",
        content:
          "These aren't edge cases. This is the normal operating environment for dealmakers, investors, and revenue leaders. And they've been navigating it without a real intelligence layer — until now.",
      },

      // ── 3. WHAT SHADOW DOES TODAY ──────────────────────────────────────
      {
        type: "heading",
        content: "What Shadow Does Today",
      },
      {
        type: "body",
        content:
          "Shadow joins your call as your second brain. Right now, in every conversation, it gives you:",
      },
      {
        type: "bullets",
        items: [
          "Real-time fact check — a claim lands across the table. Shadow verifies it instantly. You stay composed. You know what's accurate before you respond.",
          "Follow-up questions — Shadow surfaces the next question for deeper discovery before you've thought to ask it. Better discovery leads to better decisions.",
          "Live context — it pulls from your notes, past calls, and data sources mid-conversation, giving you the sharper angle exactly when you need it.",
          "Objection handling — Shadow flags resistance early and arms you with the right framing before the moment passes.",
        ],
      },
      {
        type: "body",
        content:
          "Most professionals operate in high-stakes conversations with nothing but their preparation and their instincts. Shadow gives you a third input — live, contextual intelligence — without breaking the flow of the conversation.",
      },

      // ── 4. WHERE IT'S GOING ────────────────────────────────────────────
      {
        type: "heading",
        content: "Where It's Going",
      },
      {
        type: "body",
        content:
          "But the bigger shift is cross-session. Conversations don't happen in isolation — and neither should your intelligence.",
      },
      {
        type: "bullets",
        items: [
          "Tone drift detected across three calls with the same founder — confident on the market, increasingly hesitant on the team.",
          "A question answered one way in week one, answered differently in week four — not a lie, but a signal worth understanding.",
          "A commitment made on call two, quietly walked back on call five — trackable, if you have the right system.",
          "Patterns across sessions that no individual note-taker would catch — but a second brain that's attended every call will.",
        ],
      },
      {
        type: "body",
        content:
          "This is where Conversation Intelligence becomes a category, not a feature. A single call gives you real-time edge. Across ten calls, it gives you a picture of the counterparty that no other source can match.",
      },
      {
        type: "body",
        content:
          "The professionals who operate inside this category first will have an information advantage that compounds. Not because they're smarter or more experienced — but because they're working with a fundamentally different quality of data.",
      },
      {
        type: "body",
        content:
          "Everyone else is still just taking notes.",
      },
    ],
    cta: "Shadow is AI meeting intelligence for high-stakes conversations. Try out our early product at shadowlabs.ai.",
  },
];

export default blogPosts;
